import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpDown, FileText, Mail, PenLine, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PaymentControl } from '../Banks/PaymentControl';
import { eur } from './DetailKpis';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { key: 'date', label: 'Fecha' },
  { key: 'vendor', label: 'Proveedor' },
  { key: 'description', label: 'Concepto' },
  { key: 'invoice_number', label: 'Nº factura' },
  { key: 'base_amount', label: 'Base', num: true },
  { key: 'vat_amount', label: 'IVA', num: true },
  { key: 'amount', label: 'Total', num: true },
];

const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const DeleteButton = ({ row, onDeleted }) => {
  const remove = async () => {
    const label = `${row.vendor || 'Sin proveedor'} · ${fmtDate(row.date)} · ${eur(row.amount)}`;
    if (!window.confirm(`¿Eliminar este gasto?\n${label}\n\nNo se puede deshacer.`)) return;
    const res = await fetch(`${API_URL}/api/admin/gastos/${row.id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
    });
    if (!res.ok) { toast.error('No se pudo eliminar'); return; }
    toast.success('Gasto eliminado');
    onDeleted?.(row.id);
  };
  return (
    <button onClick={remove} title="Eliminar gasto" className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" data-testid={`expense-detail-delete-${row.id}`}>
      <Trash2 size={15} />
    </button>
  );
};

const invoiceHref = (url) => (url?.startsWith('http') ? url : `${API_URL}${url}`);

const SortHeader = ({ col, sort, setSort }) => (
  <th className={`px-3 py-2.5 font-medium ${col.num ? 'text-right' : 'text-left'}`}>
    <button onClick={() => setSort(s => ({ key: col.key, dir: s.key === col.key && s.dir === 'desc' ? 'asc' : 'desc' }))}
      className="inline-flex items-center gap-1 hover:text-gray-900" data-testid={`expense-detail-sort-${col.key}`}>
      {col.label}<ArrowUpDown size={12} className={sort.key === col.key ? 'text-gray-900' : 'text-gray-300'} />
    </button>
  </th>
);

export const DetailTable = ({ rows, onDeleted, onEdit, categoryConfig }) => {
  const [sort, setSort] = useState({ key: 'date', dir: 'desc' });

  const sorted = useMemo(() => {
    const col = COLUMNS.find(c => c.key === sort.key);
    const val = (r) => (col?.num ? Number(r[sort.key]) || 0 : (r[sort.key] || '').toString().toLowerCase());
    return [...rows].sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * (sort.dir === 'asc' ? 1 : -1));
  }, [rows, sort]);

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="expense-detail-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              {COLUMNS.slice(0, 2).map(c => <SortHeader key={c.key} col={c} sort={sort} setSort={setSort} />)}
              {categoryConfig && <SortHeader col={{ key: 'category', label: 'Categoría' }} sort={sort} setSort={setSort} />}
              {COLUMNS.slice(2).map(c => <SortHeader key={c.key} col={c} sort={sort} setSort={setSort} />)}
              <th className="px-3 py-2.5 text-left font-medium">Origen</th>
              <th className="px-3 py-2.5 text-left font-medium">Pago</th>
              <th className="px-3 py-2.5 text-center font-medium">Factura</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {sorted.length === 0 && (
              <tr><td colSpan={12} className="text-center text-gray-400 py-10">No hay gastos con estos filtros</td></tr>
            )}
            {sorted.map(r => (
              <tr key={r.id} className="hover:bg-gray-50 transition-colors" data-testid={`expense-detail-row-${r.id}`}>
                <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-3 py-2.5 font-medium text-gray-900 max-w-[180px] truncate" title={r.vendor}>
                  {r.vendor ? (
                    <Link to={`/admin/gastos/proveedor/${encodeURIComponent(r.vendor)}`} className="hover:underline" data-testid={`expense-detail-vendor-link-${r.id}`}>{r.vendor}</Link>
                  ) : '—'}
                </td>
                {categoryConfig && (
                  <td className="px-3 py-2.5 whitespace-nowrap" data-testid={`expense-detail-category-${r.id}`}>
                    <Link to={`/admin/gastos/categoria/${r.category}`} className="text-xs font-semibold px-2 py-0.5 rounded-full hover:opacity-80"
                      style={{ color: (categoryConfig[r.category] || categoryConfig.otros)?.color, backgroundColor: `${(categoryConfig[r.category] || categoryConfig.otros)?.color}1a` }}>
                      {(categoryConfig[r.category] || categoryConfig.otros)?.label || r.category}
                    </Link>
                  </td>
                )}
                <td className="px-3 py-2.5 text-gray-600 max-w-[260px] truncate" title={r.description}>{r.description || '—'}</td>
                <td className="px-3 py-2.5 text-gray-500 font-mono text-xs">{r.invoice_number || '—'}</td>
                <td className="px-3 py-2.5 text-right text-gray-600">
                  {r.base_amount ? eur(r.base_amount) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title="Sin base imponible: revisa si es una factura real" data-testid={`expense-detail-no-base-${r.id}`}>sin base</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-600">{r.vat_amount != null ? eur(r.vat_amount) : '—'}</td>
                <td className="px-3 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">
                  {r.alert_pct != null && (
                    <span className="mr-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" title="Más alto de lo normal para este proveedor" data-testid={`expense-detail-alert-${r.id}`}>+{r.alert_pct}%</span>
                  )}
                  {eur(r.amount)}
                </td>
                <td className="px-3 py-2.5">
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                    {r.source === 'gmail' ? <Mail size={12} /> : <PenLine size={12} />}
                    {r.source === 'gmail' ? 'Gmail' : 'Manual'}
                    {r.status === 'pendiente' && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">pendiente</span>}
                    {r.possible_duplicate && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid={`expense-detail-duplicate-${r.id}`}>duplicado?</span>}
                  </span>
                </td>
                <td className="px-3 py-2.5"><PaymentControl expense={r} /></td>
                <td className="px-3 py-2.5 text-center">
                  {r.invoice_url?.startsWith('/api/uploads/') ? (
                    <span className="text-xs text-amber-600" title="Archivo antiguo perdido: vuelve a adjuntarlo desde Gastos" data-testid={`expense-detail-invoice-missing-${r.id}`}>No disponible</span>
                  ) : r.invoice_url ? (
                    <a href={invoiceHref(r.invoice_url)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                      data-testid={`expense-detail-invoice-${r.id}`}>
                      <FileText size={15} /> Ver
                    </a>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-2 py-2.5 text-center whitespace-nowrap">
                  {onEdit && (
                    <button onClick={() => onEdit(r)} title="Editar gasto" className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" data-testid={`expense-detail-edit-${r.id}`}>
                      <Pencil size={15} />
                    </button>
                  )}
                  <DeleteButton row={r} onDeleted={onDeleted} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
